import React, { useCallback, useEffect, useState } from 'react';
import { Center, Heading, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useNetworkStatus } from 'clipsal-cortex-ui/src/components/NetworkStatusProvider';
import { useOnMount } from 'clipsal-cortex-utils/src/hooks/use-on-mount';

import { RTKQError } from '../../../../../common/api/api-helpers';
import { ArcButton } from '../../../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../../../common/components/SlidingAnimationPageBase';
import { TopNavProgressLoader } from '../../../../../common/components/TopNavProgressLoader';
import { useParamBackUrl } from '../../../../../common/hooks/use-param-back-url';
import { ErrorStatusIcon } from '../../../../../styles/custom-icons';
import { MANUFACTURER_ID_AYLA } from '../../../../devices/devices-helper';
import {
  useGetSwitchesByManufacturerQuery,
  useRegisterAylaDeviceMutation,
} from '../../../../devices/switches/switchApi';
import { selectSite } from '../../../../site/siteSlice';

type RegistrationStatus = 'IN_PROGRESS' | 'REGISTRATION_FAILED';

// This component is used after any local device configuration, to register the device via the API
export function AylaDeviceRegistration() {
  const { site_id: siteId } = useSelector(selectSite);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const deviceSerialNumber = search.get('deviceSerialNumber');
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus>('IN_PROGRESS');
  const backURL = useParamBackUrl('../start?direction=back');
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const [registerAylaDevice, { isLoading: isRegistrationLoading }] = useRegisterAylaDeviceMutation();
  const {
    data: aylaSwitchesFromApi,
    isLoading: isSwitchesLoading,
    refetch,
  } = useGetSwitchesByManufacturerQuery([MANUFACTURER_ID_AYLA]);
  const { isOnline } = useNetworkStatus();
  const isLoaded = !isSwitchesLoading && !isRegistrationLoading;

  // Refetch the Ayla switches when the network status changes to online
  // This is useful in case the user was offline during the registration and then comes back online
  useEffect(() => {
    if (isOnline) refetch();
  }, [isOnline, refetch]);

  const registerDevice = useCallback(async () => {
    try {
      // Before registration, check if the user is connected to internet first since they might need to manually -
      // -- rejoin their wifi network after local device configuration (we can't do it for them)
      /* istanbul ignore if -- @preserve */
      if (!isOnline) {
        setRegistrationStatus('REGISTRATION_FAILED');
        toast({
          title: t('Common.not connected to internet'),
          description: t('Set Up Hardware.you may need to reconnect'),
          status: 'info',
        });
        return;
      }

      if (!deviceSerialNumber) {
        throw new Error('Missing serial number for registration');
      }

      const deviceAlreadyRegistered = aylaSwitchesFromApi.find(
        (aylaSwitch) => aylaSwitch.oem_switch_id === deviceSerialNumber
      );
      if (deviceAlreadyRegistered) {
        navigate(`../../../matter-devices/${deviceAlreadyRegistered.id}/view`);
        return;
      }

      const registeredDevice = await registerAylaDevice({
        siteId,
        deviceSerialNumber,
      }).unwrap();

      toast({
        title: t('Devices.successfully added'),
        status: 'success',
        id: 'device-registration-success',
      });
      navigate(`../../../matter-devices/${registeredDevice.id}/view`);
    } catch (e) {
      if ((e as RTKQError)?.status === 409) {
        toast({
          title: t('Devices.device already registered title'),
          description: t('Devices.device already registered message'),
          status: 'error',
          id: 'device-registration-error',
        });
      } else {
        Sentry.captureException(e, {
          tags: {
            category: 'feature',
            feature: 'ayla-switches',
            component: 'AylaDeviceRegistration',
            method: 'registerDevice',
          },
        });
      }
      setRegistrationStatus('REGISTRATION_FAILED');
    }
  }, [deviceSerialNumber, aylaSwitchesFromApi, registerAylaDevice, siteId, toast, t, navigate, isOnline]);

  useOnMount(registerDevice);

  let content;

  if (registrationStatus === 'IN_PROGRESS') {
    content = (
      <>
        <TopNavProgressLoader />
        <Heading mt={3} size="md">
          {t('Devices.commissioning in progress')}...
        </Heading>
        <Text mt={2}>{t('Set Up Hardware.give us a minute')}</Text>
      </>
    );
  }

  if (registrationStatus === 'REGISTRATION_FAILED') {
    content = (
      <>
        <ErrorStatusIcon height={200} width={200} />
        <Heading size="md" mt={4} data-testid="register-error-title">
          {t('Devices.error registering device')}
        </Heading>
        <Text>{t('Common.if this persists contact support')}</Text>
        <ArcButton
          onClick={registerDevice}
          mt={8}
          w={'80%'}
          arcColor="#3DCD57"
          data-testid="try-again-button"
          isLoading={!isLoaded}
        >
          {t('Common.try again')}
        </ArcButton>
      </>
    );
  }

  return (
    <SlidingAnimationPageBase title={t('Common.device setup')} backURL={backURL}>
      <Center flexDirection="column" px={2} mt={6} textAlign="center">
        {content}
      </Center>
    </SlidingAnimationPageBase>
  );
}
